import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faAddressCard } from "@fortawesome/pro-regular-svg-icons/faAddressCard";
import { faCircleNodes } from "@fortawesome/pro-regular-svg-icons/faCircleNodes";
import { faCommentQuestion } from "@fortawesome/pro-regular-svg-icons/faCommentQuestion";
import { faMessagePen } from "@fortawesome/pro-regular-svg-icons/faMessagePen";
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons/faLocationDot";
import { faMobileScreenButton } from "@fortawesome/pro-solid-svg-icons/faMobileScreenButton";
import { faPhone } from "@fortawesome/pro-solid-svg-icons/faPhone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Dialog, DialogContent, DialogTitle, Grid, Skeleton, styled, Typography } from "@mui/material";
import { APP_PAGE_SIZE_MEDIUM, APP_PAGE_SIZE_SHORT } from "app/appConstants";
import { AppImages } from "app/appImages";
import { MprcButton, MprcRequestErrorDialog, MprcRequestErrorReport } from "components";
import { IconButtonClose } from "components/iconButtons";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { FeedPanelUserCredentialsResponse, PublicationType, SavePublicationResultDto, UserCredentialTypeSelectorResponse, UserPanelPersonalInterestDtoPaginatedList, UserPanelProfessionaInterestDtoPaginatedList, UserPhoneType, UserProfileDto, UserStatus } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { LRUCache } from 'typescript-lru-cache';
import { NewDirectMessageDialog } from "views/foundation/directMessage/NewDirectMessageDialog";
import { PanelUserCredentials } from "views/knowledge/feed/components/Panel/PanelUserCredentials";
import { PanelUserPersonalInterest } from "views/knowledge/feed/components/Panel/PanelUserPersonalInterest";
import { PanelUserProfessionalInterest } from "views/knowledge/feed/components/Panel/PanelUserProfessionalInterest";
import { publicationFactory } from "views/knowledge/feed/FeedHelpers";
import { BlockedUserProfileButton, EditProfileButton, FollowUserProfileButton, ShareUserProfileButton, SilencedUserProfileButton, ToggleMutedUserProfileButton, UnfollowUserProfileButton } from "views/knowledge/feed/FeedUserProfile/defaults";
import { UserProfileRequestConnectionDialog } from "views/knowledge/feed/FeedUserProfile/UserProfileRequestConnectionDialog";

////////////////////////////////////////////////////////////////////////////////

const MainContainer = styled("div",
    { shouldForwardProp: (propName: string) => !["imageUrl"].includes(propName) }
)<{ imageUrl: string; }>(
    ({ theme, imageUrl }) => ({
        width: "100%",
        minWidth: "716px",
        position: "relative",
        backgroundImage: `url("${imageUrl}")`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        boxShadow: "2px 2px 20px rgba(0, 0, 0, 0.2)"
    })
);

const ContentContainer = styled("div")(
    ({ theme }) => ({
        width: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        color: "#fff",
        boxShadow: "2px 2px 20px rgba(0, 0, 0, 0.2)"
    })
);

/////////////////////////////////////////////////////////////////////////////////

const LoadingSkeleton = () => {
    return (

        <>
            <MainContainer
                imageUrl={AppImages.UserProfileBackground}
            >
                <ContentContainer>
                    <Grid container direction="column" padding={2} wrap="nowrap" >
                        <Grid>
                            <Grid container spacing={3} mb={1} wrap="nowrap">
                                <Grid>
                                    <Avatar
                                        src={AppImages.UserThumbnail}
                                        sx={{ marginLeft: 2, width: "135px", height: "135px" }}
                                    />
                                </Grid>
                                <Grid>
                                    <Grid container alignItems="start" rowSpacing={1} >
                                        <Grid size={{ xs: 12 }}>
                                            <Skeleton variant="rectangular" width={310} height={22} />
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Skeleton variant="rectangular" width={210} height={22} />
                                        </Grid>
                                        <Grid>
                                            <Skeleton variant="rectangular" width={210} height={22} />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid>
                            <Skeleton variant="rectangular" width={500} height={36} />
                        </Grid>
                    </Grid>
                </ContentContainer>
            </MainContainer>
        </>
    );
};

/////////////////////////////////////////////////////////////////////////////////

interface IHeaderProps {
    user: UserProfileDto | null;
    onBeforeLinkClick?: () => void;
    onReset: (user?: UserProfileDto) => void;
    onClose: () => void;
}

const UserProfileHeader: React.FC<IHeaderProps> = (props) => {
    const { onBeforeLinkClick, onClose } = props;
    const user = props.user!;

    const session = useSession();
    const navigate = useNavigate();
    const { t } = useTranslation("knowledge");

    const [dialog, setDialog] = useState<any>();

    ///
    const handleClose = (newPub: SavePublicationResultDto | null) => {
        setDialog(null);
    };

    ///
    const handleQuestion = () => {
        setDialog(publicationFactory(
            null,
            PublicationType.Question,
            handleClose,
            { responderId: user.userId, responderName: user.name }
        ));
    };

    ///
    const handleRequestConnection = () => {
        setDialog(
            <UserProfileRequestConnectionDialog
                userId={user.userId}
                name={user.name}
                onClose={(saved) => {
                    if (saved) props.onReset();
                    setDialog(null);
                }}
            />
        );
    };

    ///
    const handleDirectMessage = () => {
        setDialog(
            <NewDirectMessageDialog
                recipients={[{ key: user.userId, label: user.name, thumbnailUrl: user.thumbnailUrl } as AutoCompleteDataModel]}
                onClose={() => setDialog(null)}
            />
        );
    };

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    const RenderIconText = (text: string, icon: IconDefinition) => {
        return (
            <Grid container spacing={1}>
                <Grid>
                    <Typography variant="subtitle1" color="white">
                        <FontAwesomeIcon icon={icon} color="white" />
                    </Typography>
                </Grid>
                <Grid size="grow">
                    <Typography variant="subtitle1" color="white">{text}</Typography>
                </Grid>
            </Grid>
        );
    };

    const CaptionBullets = (text: string) => {
        return (
            <Typography component="p" variant="subtitle1" color="white" ml={2}
                sx={{
                    listStyleType: "disc",
                    display: "list-item",
                }}>
                <b>{text}</b>
            </Typography>
        );
    };

    return (

        <>
            {dialog}
            <MainContainer
                imageUrl={user.backgroundImage?.url
                    ? user.backgroundImage?.url
                    : AppImages.UserProfileBackground
                }
            >
                <ContentContainer>
                    <Grid container direction="column" padding={2} wrap="nowrap" width="100%">
                        <Grid>
                            <Grid container spacing={3} mb={1} wrap="nowrap">
                                <Grid>
                                    <Avatar
                                        src={
                                            user?.thumbnailUrl
                                                ? user.thumbnailUrl
                                                : AppImages.UserThumbnail
                                        }
                                        sx={{ marginLeft: 2, width: "135px", height: "135px" }}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <Grid container alignItems="start" rowSpacing={1}>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="h2" textTransform="unset" color="white" marginRight={2} noWrap>{user?.name}</Typography>
                                        </Grid>
                                        {
                                            user.status != UserStatus.Deleted &&
                                            <Grid size={{ xs: 12 }}>
                                                <Grid container spacing={1} alignItems="center">
                                                    {user?.units?.map((item, index) => {
                                                        return (
                                                            <Grid key={item.unitId}>
                                                                {
                                                                    index == 0
                                                                        ? <Typography variant="subtitle1" component="p" color="white"><b>{item.name}</b></Typography>
                                                                        : CaptionBullets(item.name)
                                                                }
                                                            </Grid>
                                                        );
                                                    })
                                                    }
                                                </Grid>
                                            </Grid>
                                        }
                                        {
                                            user.status != UserStatus.Deleted &&
                                            <Grid>
                                                {
                                                    user?.workplaceText != undefined && user?.workplaceText != null && user?.workplaceText.length > 0 &&
                                                    RenderIconText(user.workplaceText, faLocationDot)
                                                }
                                                {user?.email != undefined && user?.email.length > 0 && RenderIconText(user.email, faEnvelope)}
                                                {user?.phones != undefined && user.phones.length > 0 && (
                                                    <Grid container columnSpacing={2}>
                                                        {user.phones.map((item, index) => (
                                                            <Grid key={index}>
                                                                {RenderIconText(
                                                                    item.phoneNumber,
                                                                    item.phoneType === UserPhoneType.Mobile ? faMobileScreenButton : faPhone
                                                                )}
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                )}
                                            </Grid>
                                        }
                                    </Grid>
                                    {(user.status == UserStatus.Inactive || user.status == UserStatus.Deleted) &&
                                        <Grid sx={{ mt: 1 }}>
                                            <Typography variant="caption" color="error" textTransform="uppercase">{user.statusName}</Typography>
                                        </Grid>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                        {
                            user.status != UserStatus.Inactive && user.status != UserStatus.Deleted &&
                            <Grid >
                                <Grid container justifyContent="space-between" padding={1}>
                                    <Grid>
                                        <Box sx={{
                                            minWidth: "135px",
                                            display: "flex",
                                            justifyContent: "center",
                                        }}>
                                            {
                                                user?.isCurrentUser
                                                    ? <EditProfileButton onBeforeClick={onBeforeLinkClick} />
                                                    : user?.blockedOn != null
                                                        ?
                                                        <BlockedUserProfileButton user={user} onSuccess={(user) => props.onReset(user)} />
                                                        : user?.silencedOn != null
                                                            ? <SilencedUserProfileButton user={user} onSuccess={(user) => props.onReset(user)} />
                                                            : user?.isFollowing
                                                                ?
                                                                <>
                                                                    <UnfollowUserProfileButton user={user} onSuccess={() => props.onReset({ ...user, isFollowing: false })} />
                                                                    <Box sx={{ display: "flex", justifyContent: "center", minWidth: "50px" }}>
                                                                        <ToggleMutedUserProfileButton
                                                                            user={user}
                                                                            onSuccess={() => props.onReset({ ...user, muted: !user!.muted })}
                                                                        />
                                                                    </Box>
                                                                </>
                                                                :
                                                                <FollowUserProfileButton user={user} onSuccess={() => props.onReset({ ...user, isFollowing: true })} />
                                            }
                                        </Box>
                                    </Grid>
                                    <Grid>
                                        <MprcButton
                                            startIcon={<FontAwesomeIcon icon={faAddressCard} />}
                                            size="large"
                                            text={t("Feed.UserProfile.FullProfileText")}
                                            sx={{ color: "white" }}
                                            onClick={
                                                () => {
                                                    onBeforeLinkClick && onBeforeLinkClick();
                                                    onClose();
                                                    navigate(RootPaths.BuildUserProfile(user.userId));
                                                }
                                            }
                                        />
                                    </Grid>

                                    {
                                        user.blockedOn == null && session.user.userId != user.userId &&
                                        <Grid>
                                            <MprcButton
                                                startIcon={<FontAwesomeIcon icon={faCommentQuestion} />}
                                                size="large"
                                                text={t("Feed.UserProfile.QuestionButtonText")}
                                                title={t("Feed.UserProfile.QuestionButtonTitle")}
                                                sx={{ color: "white" }}
                                                onClick={handleQuestion}
                                            />
                                        </Grid>
                                    }

                                    {
                                        user.blockedOn == null && session.user.userId != user.userId &&
                                        <Grid>
                                            <MprcButton
                                                startIcon={<FontAwesomeIcon icon={faMessagePen} />}
                                                size="large"
                                                text={t("Feed.UserProfile.DirectMessageText")}
                                                title={t("Feed.UserProfile.DirectMessageTitle")}
                                                sx={{ color: "white" }}
                                                onClick={handleDirectMessage}
                                            />
                                        </Grid>
                                    }

                                    {
                                        user.allowRequestConnection &&
                                        <Grid>
                                            <MprcButton
                                                startIcon={<FontAwesomeIcon icon={faCircleNodes} />}
                                                size="large"
                                                text={t("Feed.UserProfile.RequestConnectionButtonText")}
                                                title={t("Feed.UserProfile.RequestConnectionButtonTitle")}
                                                sx={{ color: "white" }}
                                                onClick={handleRequestConnection}
                                            />
                                        </Grid>
                                    }
                                    <Grid>
                                        <ShareUserProfileButton user={user} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                </ContentContainer>
            </MainContainer >
        </>
    );
};

/////////////////////////////////////////////////////////////////////////////////

const ProfilePanel: React.FC<{ user: UserProfileDto; }> = (props) => {

    const user = props.user!;
    const { userId } = user;
    const api = useCoreApi();
    const { t } = useTranslation("knowledge");
    const [viewError, setViewError] = useState<any>();
    const [professionalInterest, setProfessionalInterest] = useState<UserPanelProfessionaInterestDtoPaginatedList>();
    const [personalInterest, setPersonalInterest] = useState<UserPanelPersonalInterestDtoPaginatedList>();
    const [userCredentials, setUserCredentials] = useState<FeedPanelUserCredentialsResponse[]>();
    const [credentialTypes, setCredentialTypes] = useState<UserCredentialTypeSelectorResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    ////////////////////////////////////////////////////////////////////////////
    /// Effects
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar
    useEffect(() => {
        if (loading) {
            Promise.all([
                api.v1.knowledgeFeed.getFeedUserPanelProfessionalInterest({ userId, pageNumber: 1, pageSize: APP_PAGE_SIZE_SHORT, searchText: null }),
                api.v1.knowledgeFeed.getFeedUserPanelPersonalInterest({ userId, pageNumber: 1, pageSize: APP_PAGE_SIZE_MEDIUM, searchText: null }),
                api.v1.knowledgeFeed.getFeedPanelUserCredentials({ userId }),
                api.v1.selectors.userCredentialTypes()
            ])
                .then((results) => {
                    setProfessionalInterest(results[0].data);
                    setPersonalInterest(results[1].data);
                    setUserCredentials(results[2].data);
                    setCredentialTypes(results[3].data);
                })
                .catch((error: any) => {
                    setViewError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////
    /// Render
    ////////////////////////////////////////////////////////////////////////////

    if (viewError) {
        return (
            <DialogContent>
                <MprcRequestErrorReport error={viewError} />
            </DialogContent>
        );
    }

    if (loading || (professionalInterest?.items.length == 0 && personalInterest?.items.length == 0 && userCredentials?.length == 0))
        return null;


    return (
        <DialogContent>
            <Grid container spacing={2} width="100%">
                <Grid overflow="hidden" width="100%">
                    {
                        professionalInterest &&
                        <PanelUserProfessionalInterest
                            title={t("Feed.Panels.UserProfessionalInterest.Title")}
                            userId={userId}
                            viewData={professionalInterest!}
                            onChange={(item, index) => {
                                let newItems = [...professionalInterest.items];
                                newItems[index] = item;
                                setProfessionalInterest({ ...professionalInterest, items: newItems });
                            }}
                            onGetData={(pageSize) =>
                                new Promise<boolean>((resolve, reject) => {
                                    api.v1.knowledgeFeed.getFeedUserPanelProfessionalInterest({ userId, pageNumber: 1, pageSize, searchText: null })
                                        .then(result => {
                                            setProfessionalInterest(result.data);
                                            resolve(true);
                                        })
                                        .catch(e => reject(e));
                                })
                            }
                        />
                    }

                    {
                        personalInterest &&
                        <PanelUserPersonalInterest
                            viewData={personalInterest}
                            onGetData={(pageSize) =>
                                new Promise<boolean>((resolve, reject) => {
                                    api.v1.knowledgeFeed.getFeedUserPanelPersonalInterest({ userId, pageNumber: 1, pageSize, searchText: null })
                                        .then(result => {
                                            setPersonalInterest(result.data);
                                            resolve(true);
                                        })
                                        .catch(e => reject(e));
                                })
                            }
                        />
                    }
                </Grid>
                {
                    userCredentials && userCredentials.length > 0 && credentialTypes &&
                    <Grid>
                        <PanelUserCredentials viewData={userCredentials} credentialTypes={credentialTypes} />
                    </Grid>
                }
            </Grid>
        </DialogContent>
    );
};

/////////////////////////////////////////////////////////////////////////////////

interface IPros {
    userId: string | null;
    onClose: () => void;
    onBeforeLinkClick?: () => void;
}

export const UserProfileDialog = (props: IPros) => {
    const {
        userId,
        onClose,
        onBeforeLinkClick
    } = props;

    const api = useCoreApi();
    const [user, setUser] = useState<UserProfileDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const cache = useRef<LRUCache<string, UserProfileDto>>(
        new LRUCache<string, UserProfileDto>({
            maxSize: 10,
            entryExpirationTimeInMS: 300000 // 5 minutos
        })
    );

    ///
    const load = (reload?: boolean) => {

        const u = cache.current.get(userId!);
        if (u == null || reload) {
            setLoading(true);

            api.v1.knowledgeFeed.getFeedUserProfileHeader({ UserId: userId! })
                .then((result) => {
                    cache.current.set(userId!, result.data);
                    setUser(result.data);
                })
                .catch((error: any) => {
                    setViewError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
        else {
            setUser(u);
        }
    };

    ///
    const handleReset = (user?: UserProfileDto) => {
        if (user) {
            setUser(user);
        }
        else if (userId) {
            load();
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        if (userId != null && (user == undefined || user.userId != userId))
            load();
    }, [userId]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    if (userId == null) {
        return <></>;
    }

    return (
        viewError
            ? <MprcRequestErrorDialog error={viewError} onClose={() => { setViewError(null); onClose(); }} />
            : <Dialog
                open={userId != null}
                fullWidth
                maxWidth="md"
                onClose={onClose}
            >
                <DialogTitle component="div" sx={{
                    m: 0,
                    p: 0,
                    textTransform: "unset",
                    "&.MuiDialogTitle-root+.MuiDialogContent-root": { paddingTop: 2 }
                }}>
                    <Box position="relative">
                        <Box position="absolute" right="2px" zIndex="2">
                            <IconButtonClose color="white" size="large" onClick={onClose} />
                        </Box>
                        {
                            loading || user == undefined
                                ? <LoadingSkeleton />
                                : <UserProfileHeader
                                    user={user}
                                    onBeforeLinkClick={onBeforeLinkClick}
                                    onReset={handleReset}
                                    onClose={onClose}
                                />
                        }
                    </Box>
                </DialogTitle>
                {user != undefined && !loading && <ProfilePanel user={user} />}
            </Dialog>
    );
};
